// The status a turn ends on: the one rule the run service records on its row
// (server/src/execution/run-status.ts, which re-exports it) and every fold of the run's log
// reads (the session view, run-view.ts). Pure. What feeds it, the turn's facts since the
// person's last message, each caller keeps its own way: the server on its projection with
// Sets, the view in its plain JSON fold. The step that reads an error is shared as well
// (`turnErrorAfter`), so that reading is one rule too.
//
// The work is done only when every workflow run the session created has ended and, after that,
// an idle with end_turn arrives that the person did not cause (the Workflow runs page,
// workflow-runs.md:151-161). While a run is open an idle is never a terminal status, and after
// an interrupt in a turn that had a run, only an idle after the person's next message or
// outcome counts. A thread the organization's per-minute limit stopped (workflow-runs.md:835)
// keeps the run open: the turn's end reads `requires_action` / `org_rate_limited` until the
// person, after a minute, asks the agent to continue.
import { idleAtOrgRateLimit, isOrgRateLimitStop, RATE_LIMITED_STOP } from "./run-events.js";
/** The stop reason recorded for an idle that names none; no engine sends such an idle. */
export const UNKNOWN_STOP = "unknown";
/** The idle's stop reason as the engines send it (`{ type }`); `UNKNOWN_STOP` when it names none. */
export function stopReasonOf(event) {
    const reason = event?.stop_reason;
    const type = reason && typeof reason === "object" ? reason.type : undefined;
    return typeof type === "string" ? type : UNKNOWN_STOP;
}
/** An idle that pauses the turn: it waits on an answer (`requires_action`) or the budget. */
export function isPauseStop(stop) {
    return stop === "requires_action" || stop === "budget_reached";
}
/** A grader verdict that is also a run status; `interrupted` is covered by the interrupt flag. */
const VERDICT_STATUSES = new Set([
    "satisfied",
    "needs_revision",
    "max_iterations_reached",
    "failed",
]);
/** The agent spoke or acted again: whatever error came before did not end the turn. */
const AGENT_PROGRESS = new Set([
    "agent.message",
    "agent.thinking",
    "agent.tool_use",
    "agent.mcp_tool_use",
    "agent.custom_tool_use",
]);
/**
 * The turn's error after one more row of the run's log (`payload`: the row's body). A
 * primary-thread `session.error` the platform is not retrying sets it, by its type; the
 * agent's next primary-thread event or the person's next message clears it: the turn went on.
 * A helper's error carries its thread and stays in its lane: the lead's turn decides the run.
 * The per-minute limit's stop is neither: the person continues it (`TurnFacts.rateLimited`).
 */
export function turnErrorAfter(current, type, payload) {
    if (type === "user.message" || type === "user.define_outcome")
        return null;
    const row = payload && typeof payload === "object" ? payload : {};
    if (row.session_thread_id != null)
        return current;
    if (AGENT_PROGRESS.has(type))
        return null;
    if (type !== "session.error" || isOrgRateLimitStop(payload))
        return current;
    const error = row.error && typeof row.error === "object" ? row.error : null;
    const retry = error?.retry_status;
    // CMA says when it is still retrying; that error has not ended anything yet.
    if (retry && typeof retry === "object" && retry.type === "retrying")
        return current;
    return typeof error?.type === "string" ? error.type : "error";
}
/**
 * The status a `session.status_idle` sets. While a workflow run is open the idle is not the end
 * of the work: a pause (`requires_action`, `budget_reached`, the per-minute limit's
 * `org_rate_limited`) and an interrupt read `requires_action` (after an interrupt the person says
 * whether the runs go on or stop), and any other idle reads `running` (the run works on, and
 * when it ends the lead gets a turn to read it, or after `retries_exhausted` the server starts
 * one). With none open, an idle after an interrupt in a turn that had a run is not the end
 * either (only an idle after the person's next message or outcome counts): `requires_action`,
 * the person continues. Otherwise `endingAt` decides.
 */
export function statusAtIdle(turn, stop) {
    const ending = endingAt(turn, stop);
    if (turn.openRuns.size === 0 && !(turn.interrupted && turn.runsInTurn))
        return ending;
    // A pause keeps its own stop reason (the per-minute limit's is `org_rate_limited`).
    if (ending.status === "requires_action")
        return ending;
    if (turn.interrupted)
        return { status: "requires_action", stopReason: stop };
    return { status: "running", stopReason: stop };
}
/**
 * How an idle ends the run with no workflow run open. A pause (`requires_action`,
 * `budget_reached`) and an interrupt come first; then a turn that ended while a thread the
 * per-minute limit stopped had not worked again (`requires_action`, `org_rate_limited`: the run
 * stays open for the person's Continue); then the endings that are failures whatever
 * the grader said earlier (`retries_exhausted`, `refusal`, a stop reason no engine sends, or
 * none); then the grader's verdict; and an `end_turn` with no verdict is `failed` when an error
 * ended the turn, else `satisfied`. The stop reason recorded is the engine's, except that an
 * error which ended the turn is recorded by its type: the engine's own `end_turn` would hide it.
 */
function endingAt(turn, stop) {
    if (isPauseStop(stop))
        return { status: "requires_action", stopReason: stop };
    if (turn.interrupted)
        return { status: "interrupted", stopReason: stop };
    if (idleAtOrgRateLimit(turn.rateLimited, stop))
        return { status: "requires_action", stopReason: RATE_LIMITED_STOP };
    if (stop !== "end_turn")
        return { status: "failed", stopReason: stop };
    if (turn.lastOutcome && VERDICT_STATUSES.has(turn.lastOutcome))
        return { status: turn.lastOutcome, stopReason: stop };
    if (turn.turnError)
        return { status: "failed", stopReason: turn.turnError };
    return { status: "satisfied", stopReason: stop };
}
//# sourceMappingURL=run-status.js.map